import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { addToastNotification } from 'redux/actions';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import { getErrorMessage, isObjectEmpty, logger } from 'common/utils/helpers';
import { selectUserPreferences } from 'redux/selectors';
import { selectUserPreferenceList, createUserPreference } from 'redux/UserPreferences';
import { DownloadIcon } from 'components/icons';
import Button from 'components/shared/Button';
import { ProgressIndicator } from 'components/shared/Progress';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import { generateUniqueName, validateChartSetJSONData } from './utils';
import styles from './ChartSetPreferenceImport.module.css';

function ChartSetPreferenceImport() {
  const dispatch = useDispatch();
  const importFileRef = useRef(null);
  const [importedChartSet, setImportedChartSet] = useState(null);
  const [importedCharts, setImportedCharts] = useState(null);
  const [createChartPreferenceError, setCreateChartPreferenceError] = useState(null);
  const [createChartSetPreferenceError, setCreateChartSetPreferenceError] = useState(null);
  const [actionPending, setActionPending] = useState(false);

  const allChartPreferenceNames = useSelector((state) => {
    const allCharts = selectUserPreferenceList(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART);
    return allCharts.map((chart) => chart.name);
  });

  const allChartSetPreferenceNames = useSelector((state) => {
    const allChartSets = selectUserPreferenceList(selectUserPreferences(state), USER_PREFERENCE_SETS.CHART_SET);
    return allChartSets.map((chartSet) => chartSet.name);
  });

  const handleImportJsonFile = useCallback(
    (event) => {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        const data = JSON.parse(content);

        const validationResult = validateChartSetJSONData(data);

        if (typeof validationResult === 'string') {
          dispatch(
            addToastNotification({
              id: v4(),
              title: 'Import Error: ',
              description: validationResult,
            })
          );
        } else {
          // check and update duplicated chartSet/chart name(s)
          data.chartSet.name = generateUniqueName(allChartSetPreferenceNames, data.chartSet.name);
          const originalChartNames = data.charts.map((chart) => chart.name);
          for (let i = 0; i < originalChartNames.length; i++) {
            const newChartName = generateUniqueName(allChartPreferenceNames, originalChartNames[i]);
            data.charts[i].name = newChartName;
          }
          setImportedChartSet(data.chartSet);
          setImportedCharts(data.charts);
        }
      };
      reader.readAsText(file);
    },
    [allChartSetPreferenceNames, allChartPreferenceNames, dispatch]
  );

  // create chart preferences with the imported charts data
  const handleCreateChartPreferences = useCallback(
    async (chartsData = []) => {
      if (chartsData?.length === 0) return;
      setActionPending(true);
      const allChartPreferences = [];
      for (let chart of chartsData) {
        if (chart.local === true || chart.preferenceId === 'chart_placeholder') {
          allChartPreferences.push(chart);
          continue;
        }
        await dispatch(createUserPreference(chart))
          .then((result) => {
            allChartPreferences.push(result);
          })
          .catch((error) => {
            logger.error(error);
            setActionPending(false);
            setCreateChartPreferenceError(error);
          });
      }

      // collect the newly created chart preferences along with the preferenceIds
      const newChartsState = allChartPreferences.map((chart) => {
        return {
          name: chart.name,
          category: chart.category,
          preferenceId: chart.preferenceId,
          ...(chart?.local && { local: chart.local, preferenceId: chart.preferenceId })
        };
      });

      // update chartSet with the newly created chart preferences
      if (importedChartSet) {
        const newChartSet = {
          ...importedChartSet,
          state: {
            ...importedChartSet.state,
            charts: newChartsState
          }
        };
        setImportedChartSet(newChartSet);
      }
    },
    [dispatch, importedChartSet]
  );

  // create new chartSet preference with the updated imported chartSet data
  const handleCreateChartSetPreference = useCallback(
    async (chartSetData = {}) => {
      if (isObjectEmpty(chartSetData)) return;
      await dispatch(createUserPreference(chartSetData))
        .then((result) => {
          setActionPending(false);
        })
        .catch((error) => {
          logger.error(error);
          setActionPending(false);
          setCreateChartSetPreferenceError(error);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (importedCharts?.length > 0) {
      // start creating chart preferences
      handleCreateChartPreferences(importedCharts);
      setImportedCharts(null);
    }
  }, [importedCharts, handleCreateChartPreferences]);

  useEffect(() => {
    if (importedChartSet) {
      // check if the importedChartSet has been updated with the newly created chart preferenceIds
      const havePreferenceIds = importedChartSet.state.charts.every((chart) => {
        return chart.hasOwnProperty('preferenceId');
      });
      if (havePreferenceIds) {
        // start creating chartSet preference
        handleCreateChartSetPreference(importedChartSet);
        setImportedChartSet(null);
      }
    }
  }, [importedChartSet, handleCreateChartSetPreference]);

  const onCloseErrorBox = () => {
    createChartPreferenceError && setCreateChartPreferenceError(null);
    createChartSetPreferenceError && setCreateChartSetPreferenceError(null);
    importedChartSet && setImportedChartSet(null);
    importedCharts && setImportedCharts(null);
  };

  return (
    <div className={styles.container}>
      {actionPending && <ProgressIndicator />}
      {createChartPreferenceError && (
        <CommissionErrorBox
          title={`Create Chart Error: ${createChartPreferenceError}`}
          message={getErrorMessage(createChartPreferenceError)}
          onClose={() => onCloseErrorBox()}
        />
      )}
      {createChartSetPreferenceError && (
        <CommissionErrorBox
          title={`Create Chart Set Error: ${createChartSetPreferenceError}`}
          message={getErrorMessage(createChartSetPreferenceError)}
          onClose={() => onCloseErrorBox()}
        />
      )}
      <div className={styles.import} data-tip={'Import Chart Set'}>
        <input ref={importFileRef} type="file" accept=".json" hidden={true} onChange={handleImportJsonFile} />
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          start={<DownloadIcon size="small" />}
          onClick={() => {
            if (importFileRef.current) {
              importFileRef.current.click();
            }
          }}
        >
          <span className={styles.importButtonText}>Import</span>
        </Button>
      </div>
    </div>
  );
}

export default ChartSetPreferenceImport;
